import { createFileRoute, type ErrorComponentProps } from '@tanstack/react-router'
import { useState } from 'react'

import RouteErrorPage from '@/components/layout/RouteErrorPage'
import ScreenLayout from '@/components/layout/ScreenLayout'
import CardState from '@/components/ui/CardState'
import EmptyState from '@/components/ui/EmptyState'
import Pagination from '@/components/ui/Pagination'
import Skeleton from '@/components/ui/Skeleton'
import RefreshRegion from '@/components/ui/RefreshRegion'
import TitleRow from '@/components/ui/TitleRow'
import UpdatingNote from '@/components/ui/UpdatingNote'
import { emptyArchiveMessage } from '@/features/newsletter/api/errors'
import { NEWSLETTER_PAGE_SIZE } from '@/features/newsletter/api/newsletterService'
import {
  useNewsletterArchive,
  useSuspenseLatestNewsletter,
} from '@/features/newsletter/api/queries'
import GetNewIssuesRow from '@/screens/newsletter/components/GetNewIssuesRow'
import IssueCard from '@/screens/newsletter/components/IssueCard'
import NewsletterGrid from '@/screens/newsletter/components/NewsletterGrid'
import { loadNewsletters } from '@/screens/newsletter/NewslettersScreen.loader'

/**
 * Sized against the loaded body's own shape, matching the captured render and
 * the brief's "skeleton of the real layout, not a spinner".
 *
 * Corrected in the wave 4 fidelity pass, against the Loading frame
 * (`3940:49306`) at full resolution rather than the thumbnail earlier waves
 * had: every masthead row in the real layout leads with the round mascot
 * mark, and the headline is two lines, not one — `IssueCard`'s own headline
 * is `line-clamp-2`. The previous skeleton drew a single masthead bar with no
 * circle and a single headline bar, which undersold both.
 *
 * FIDELITY PASS (wave 5), now confirmed live on `3940:49306` (`get_variable_
 * defs` + `get_design_context` both succeeded this pass): the skeleton cards
 * sit on `--bg-canvas` (`bg-background`), not `--bg-surface` — a deliberate
 * difference from the loaded cards' `bg-card`, so the skeleton recedes into
 * the page instead of reading as a filled card too early. The previous build
 * used `bg-card` for the skeleton shell, which is corrected below. Grid gap
 * is `gap-6` (24px, matching `NewsletterGrid`'s own live-measured value),
 * not `gap-5`. The individual bar widths remain a visual approximation — the
 * frame does not bind them to any measurable token — and are unchanged.
 *
 * RECOMPOSED AGAINST V2, twice over:
 *
 * First pass (`4243:74061`, before the "Loading" frame had landed): the hero
 * shell and the tile grid follow `IssueCard.tsx` / `NewsletterGrid.tsx`'s own
 * V2 recompose — the hero is an unconditional stack (photo on top, no more
 * `lg:grid-cols-2`) and the tiles go two-across on the `@2xl` container query
 * rather than `sm:`/`lg:` viewport steps, since this skeleton now renders
 * inside `ScreenLayout`'s narrower main column.
 *
 * Second pass, now confirmed live against `4243:117011` ("Newsletters ·
 * Loading"), pixel-measured (extent-scanning the bar rows, not eyeballed):
 * both the hero card and every tile draw ONE MORE bar than the previous
 * build had — a two-line headline (full width, then ~45% — not the previous
 * `w-3/4` guess) followed by its own summary line(s), not a single bar doing
 * duty for both. The hero's summary is two lines (full, then ~42%); each
 * tile's is one (full only) — matching `IssueCard`'s own `line-clamp-2`
 * summary on the featured variant versus the grid variant's shorter body-xs
 * line. Tile photos are `aspect-[260/194]`, matching the loaded grid card
 * exactly, not the previous `aspect-[3/2]` approximation.
 *
 * Wave 6 (route loading): split into `HeroSkeleton` and `GridSkeleton` rather
 * than one block. `latest` and the archive's first page are now BOTH the
 * route's main data (`NewslettersScreen.loader.ts`), so the route's own
 * pending page draws both — `NewslettersPending` below composes them exactly
 * as this one block used to. `GridSkeleton` on its own is what the grid falls
 * back to if `archiveQuery` is ever mounted with no data yet (page one is
 * loader-guaranteed, so in practice this is a safety net, not a state a
 * member sees) — `HeroSkeleton` never needs that second life: `latest` is a
 * suspense read, always defined once the screen mounts at all.
 */
function HeroSkeleton() {
  return (
    <div className="flex flex-col overflow-hidden rounded-lg border border-border-subtle bg-background">
      <Skeleton className="h-105 w-full rounded-none" />
      <div className="flex flex-col gap-3 p-6 @lg:p-8">
        <Skeleton className="h-3 w-24" />
        {/* `size-6` (24px), not the featured mark's real 22px, and `gap-2`
            (8px), not the measured 6px — both round to the nearest 4-grid
            step for a skeleton that only approximates the loaded shape
            (2026-09-28 design refresh). */}
        <div className="flex items-center gap-2">
          <Skeleton className="size-6 shrink-0 rounded-full" />
          <Skeleton className="h-3 w-32" />
        </div>
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-2/5" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-2/5" />
        <Skeleton className="mt-auto h-3 w-28" />
      </div>
    </div>
  )
}

function GridSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-6 @2xl:grid-cols-2">
      {Array.from({ length: 8 }, (_, index) => (
        <div
          key={index}
          className="flex flex-col overflow-hidden rounded-lg border border-border-subtle bg-background"
        >
          <Skeleton className="h-66 w-full rounded-none" />
          <div className="flex flex-col gap-2 p-4">
            {/* `size-5` (20px), not the grid mark's real 18px — a tie on the
                4-grid, rounded up (2026-09-28 design refresh). */}
            <div className="flex items-center gap-2">
              <Skeleton className="size-5 shrink-0 rounded-full" />
              <Skeleton className="h-3 w-16" />
            </div>
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-2/5" />
          </div>
        </div>
      ))}
    </div>
  )
}

/** Rows-per-page choices for the archive's pager, multiples of the service's
 *  own default page size (12) rather than the generic `Pagination` list
 *  (10/20/50/100) — a member picking "24" should not land on a boundary the
 *  archive never actually paged at. */
const NEWSLETTER_PAGE_SIZES = [12, 24, 48] as const

/**
 * Static — reads nothing — so the pending, failed and loaded pages all draw the
 * real one. A TitleRow, so its `h1` goes screen-reader-only under the section
 * strip while the pitch line stays as the caption the mock draws; below `lg`,
 * where there is no strip, the title shows as ever.
 */
const HEADER = (
  <TitleRow
    title="Newsletters"
    subtitle="MoneyMitra Weekly explains the week in NEPSE in plain words. Newest issue first."
  />
)

/**
 * The route's whole-page pending skeleton: hero then grid, exactly the block
 * `ARCHIVE_SKELETON` used to draw in one piece — see `HeroSkeleton`'s own doc
 * for why it is two components now.
 */
function NewslettersPending() {
  return (
    <ScreenLayout
      pending
      pendingLabel="Loading newsletters…"
      header={HEADER}
      pageTitle="Newsletters"
    >
      <GetNewIssuesRow />
      <HeroSkeleton />
      <GridSkeleton />
    </ScreenLayout>
  )
}

/**
 * The copy this screen used for a first-load failure before it had a loader.
 * NOT drawn for `NEWSLETTER_NOT_FOUND` on `latest` — that resolves to `null`
 * rather than throwing (`latestNewsletterQueryOptions`'s own doc), so it
 * never reaches here; only a genuine failure (a 5xx, a transport error) does.
 */
function NewslettersError(props: ErrorComponentProps) {
  return (
    <RouteErrorPage
      {...props}
      header={HEADER}
      title="Couldn't load issues"
      description="Check your internet and try again."
    />
  )
}

export const Route = createFileRoute('/_app/market/newsletters')({
  loader: loadNewsletters,
  pendingComponent: NewslettersPending,
  errorComponent: NewslettersError,
  component: NewslettersScreen,
})

/**
 * Newsletters · archive (Figma `3940:43017` — the archive `content` node;
 * `AppShell` supplies the topnav/ticker/sidebar around it). The header, the
 * latest issue as a full-width hero, the rest of the archive in a grid.
 *
 * ── The hero-404-vs-empty reconciliation ────────────────────────────────────
 *
 * `GET /latest/` 404s with `NEWSLETTER_NOT_FOUND` when nothing has EVER been
 * published — an EMPTY archive, not a broken one (`errors.ts`'s own doc, and
 * the brief's INTEGRATION GAP #4), so `latestNewsletterQueryOptions` resolves
 * that ONE code to `data: null` rather than throwing it: a fresh deployment
 * renders "No issues yet" below rather than the route's failed page, which
 * would otherwise tell two contradictory stories about the exact same 404. A
 * genuine failure on `latest` (anything else) still throws, all the way to
 * `NewslettersError` above, same as a genuine failure ensuring the archive's
 * first page.
 *
 * `latest` is this route's MAIN data — the hero and the empty-vs-not decision
 * both come from it — awaited in `NewslettersScreen.loader.ts` and read here
 * as a suspense query, so it is always defined once this mounts. The archive
 * stays a PLAIN query with `keepPreviousData`: it is not URL-keyed (there is
 * no `?page=`), but a page click still changes its query key, and a suspense
 * read would re-suspend the whole route to the pending page on every click —
 * the same reason docs/route-loading.md, "Search params" gives for Stock's tab
 * panels. `UpdatingNote`/`UPDATING_DIM` mark the grid as showing the PREVIOUS
 * page while the next one is in flight, the same idiom `FloorsheetPanel.tsx`
 * uses for its own pager.
 *
 * ── RECOMPOSED AGAINST V2 (`4243:74061`, live-rendered) ─────────────────────
 *
 * This screen was built against the V1 frames on `feat/newsletter` and
 * merged into `feat/web-v2` with neither `ScreenLayout` nor the shared
 * `<MarketRail />` — a straight `flex w-full flex-col` with nothing beside
 * it. The V2 archive render shows the same NEPSE/mood/updates/popular-stocks
 * rail every other V2 screen carries, so every branch below now goes through
 * `ScreenLayout` (default rail, `header={HEADER}`) instead of hand-rolling
 * its own `flex w-full flex-col gap-6` wrapper. `IssueCard`'s featured
 * variant and `NewsletterGrid`'s tile grid were recomposed to match (their
 * own doc comments have the detail) because both narrow once the rail takes
 * the other 360px + 24px gutter of the content box.
 */
function NewslettersScreen() {
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState<number>(NEWSLETTER_PAGE_SIZE)

  const { data: latest } = useSuspenseLatestNewsletter()
  const archiveQuery = useNewsletterArchive(page, pageSize)
  const isUpdating = archiveQuery.isPlaceholderData

  if (latest === null) {
    return (
      <ScreenLayout header={HEADER} pageTitle="Newsletters">
        <EmptyState
          kind="empty"
          size="page"
          title="No issues yet"
          description={emptyArchiveMessage({ kind: 'issueNotFound' })}
        />
      </ScreenLayout>
    )
  }

  // The hero already shows the latest issue — never draw it a second time as
  // its own grid tile. Page 1 of a newest-first archive is the only page
  // this ever actually removes a row from.
  const gridIssues = (archiveQuery.data?.rows ?? []).filter(
    (row) => row.issueNumber !== latest.issueNumber,
  )

  return (
    <ScreenLayout header={HEADER} pageTitle="Newsletters">
      {/* The mock's switch row (`available = false` — see GetNewIssuesRow). */}
      <GetNewIssuesRow />
      <IssueCard issue={latest} variant="featured" />
      {/* Table and pager travel as ONE block of the stack: `Pagination` draws
          its own 24px (see its PLACEMENT note) and a parent `gap-*` would add
          to it rather than absorb it. */}
      <div>
        {archiveQuery.isError && archiveQuery.data === undefined ? (
          <CardState
            state="failed"
            size="page"
            failedTitle="Couldn't load issues"
            onRetry={() => {
              void archiveQuery.refetch()
            }}
            retrying={archiveQuery.isFetching}
          />
        ) : archiveQuery.data === undefined ? (
          // The loader always ensures page one before this mounts, so this
          // is a safety net for an evicted cache entry, not a state a member
          // is expected to see.
          <GridSkeleton />
        ) : (
          <RefreshRegion updating={isUpdating} className="flex flex-col">
            <div className="flex justify-end">
              <UpdatingNote when={isUpdating} label="Updating issues…" />
            </div>
            <NewsletterGrid issues={gridIssues} />
            {/* The captured render shows nine issues with no pager at all —
                this only appears once the archive actually outgrows one
                page, which is an ADDITION beyond what the frame draws.
                `Pagination` over `LoadMore` because the archive is genuinely
                offset-paged (`newsletterService.archive`'s own doc: "not the
                cursor paging the News feed uses"), which is exactly the
                shape `Pagination` expects — see the report for the full
                reasoning. */}
            {archiveQuery.data.total > archiveQuery.data.pageSize ? (
              <Pagination
                page={archiveQuery.data.page}
                pageSize={archiveQuery.data.pageSize}
                total={archiveQuery.data.total}
                onPageChange={setPage}
                onPageSizeChange={(nextSize) => {
                  setPageSize(nextSize)
                  setPage(1)
                }}
                pageSizes={NEWSLETTER_PAGE_SIZES}
                unit="issues"
              />
            ) : null}
          </RefreshRegion>
        )}
      </div>
    </ScreenLayout>
  )
}
